<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAuth } from '../composables/useAuth'
import { useRouter } from 'vue-router'
import AppLayout from '../layouts/AppLayout.vue'
import SupplierLogo from '../components/SupplierLogo.vue'
import { API_CONFIG } from '../config/api'

interface Product {
  id: string
  name: string
  description?: string
  pricingModel?: string | null
  priceRangeLow?: number | null
  priceRangeHigh?: number | null
  deploymentModel?: string | null
  complianceCertifications: string[]
  subcategories: { id: string; name: string; area: string }[]
  supplier: { id: string; name: string; logoUrl: string | null; websiteUrl: string | null }
}

const { getAccessTokenSilently } = useAuth()
const router = useRouter()

const products = ref<Product[]>([])
const loading = ref(true)
const error = ref('')

const q = ref('')
const view = ref<'list' | 'grid'>('list')
const selCats = ref<Set<string>>(new Set())
const selSup = ref<Set<string>>(new Set())
const selDep = ref<Set<string>>(new Set())
const selComp = ref<Set<string>>(new Set())
function toggle(set: Set<string>, v: string) { const n = new Set(set); n.has(v) ? n.delete(v) : n.add(v); return n }
const toggleCat = (v: string) => { selCats.value = toggle(selCats.value, v) }
const toggleSup = (v: string) => { selSup.value = toggle(selSup.value, v) }
const toggleDep = (v: string) => { selDep.value = toggle(selDep.value, v) }
const toggleComp = (v: string) => { selComp.value = toggle(selComp.value, v) }
function clearAll() { q.value = ''; selCats.value = new Set(); selSup.value = new Set(); selDep.value = new Set(); selComp.value = new Set() }
const anyFilter = computed(() => !!(selCats.value.size || selSup.value.size || selDep.value.size || selComp.value.size || q.value))

function prodCat(p: Product): string { return p.subcategories[0]?.name || p.subcategories[0]?.area || 'Uncategorized' }
function facet(extract: (p: Product) => string[]) {
  const m = new Map<string, number>()
  for (const p of products.value) for (const v of extract(p)) if (v) m.set(v, (m.get(v) || 0) + 1)
  return [...m.entries()].sort((a, b) => b[1] - a[1])
}
const catFacets = computed(() => facet(p => [prodCat(p)]).slice(0, 16))
const supFacets = computed(() => facet(p => [p.supplier.name]).slice(0, 16))
const depFacets = computed(() => facet(p => (p.deploymentModel ? [p.deploymentModel] : [])))
const compFacets = computed(() => facet(p => p.complianceCertifications).slice(0, 14))

const filtered = computed(() => products.value.filter(p => {
  if (q.value) { const hay = (p.name + ' ' + p.supplier.name + ' ' + prodCat(p) + ' ' + (p.description || '')).toLowerCase(); if (!hay.includes(q.value.toLowerCase())) return false }
  if (selCats.value.size && !selCats.value.has(prodCat(p))) return false
  if (selSup.value.size && !selSup.value.has(p.supplier.name)) return false
  if (selDep.value.size && !(p.deploymentModel && selDep.value.has(p.deploymentModel))) return false
  if (selComp.value.size && !p.complianceCertifications.some(c => selComp.value.has(c))) return false
  return true
}))
const supplierCount = computed(() => new Set(products.value.map(p => p.supplier.id)).size)
const complianceCount = computed(() => products.value.filter(p => p.complianceCertifications.length).length)
const shown = computed(() => filtered.value.slice(0, 200))

function pricing(p: Product): string {
  const fmt = (n: number) => n >= 1000 ? '$' + (n / 1000).toFixed(n % 1000 ? 1 : 0) + 'k' : '$' + n
  if (p.priceRangeLow && p.priceRangeHigh) return `${fmt(p.priceRangeLow)}–${fmt(p.priceRangeHigh)}`
  if (p.priceRangeLow) return `${fmt(p.priceRangeLow)}+`
  return p.pricingModel || 'Contact'
}
function openProduct(p: Product) { router.push(`/advisor/products/${p.id}`) }

onMounted(async () => {
  try {
    const token = await getAccessTokenSilently()
    const res = await fetch(`${API_CONFIG.baseURL}/api/advisors/me/product-directory`, { headers: { Authorization: `Bearer ${token}` } })
    if (!res.ok) throw new Error(`Failed to load products (${res.status})`)
    products.value = await res.json()
  } catch (e: any) { error.value = e.message || 'Failed to load products' }
  finally { loading.value = false }
})
</script>

<template>
  <AppLayout>
    <div class="cc-dir">
      <!-- Header -->
      <div class="cc-dir-head">
        <div>
          <div class="cc-dir-meta">
            <span style="width:6px;height:6px;border-radius:50%;background:var(--cc-live);box-shadow:0 0 6px var(--cc-live)"></span>
            <span>SUPPLIERS · PRODUCTS</span><span style="opacity:.5">·</span><span>{{ products.length.toLocaleString() }} INDEXED</span>
          </div>
          <h1>Product Directory</h1>
          <p>Browse every indexed product across suppliers — filter by category, deployment, and compliance.</p>
        </div>
      </div>

      <!-- Stat strip -->
      <div class="cc-dir-stats">
        <div class="cc-dir-stat"><div class="cc-dir-stat-l"><span class="cc-label">TOTAL · PRODUCTS</span><div class="cc-dir-stat-num">{{ products.length.toLocaleString() }}</div></div><div class="cc-dir-stat-spark"><svg viewBox="0 0 64 28" preserveAspectRatio="none" style="width:64px;height:28px;color:var(--cc-accent)"><polyline points="0,22 16,16 32,18 48,9 64,4" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></div></div>
        <div class="cc-dir-stat is-network"><div class="cc-dir-stat-l"><span class="cc-label">SUPPLIERS</span><div class="cc-dir-stat-num">{{ supplierCount }}</div></div><div class="cc-dir-stat-spark"><svg viewBox="0 0 64 28" preserveAspectRatio="none" style="width:64px;height:28px;color:var(--cc-accent)"><polyline points="0,20 16,18 32,14 48,10 64,6" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></div></div>
        <div class="cc-dir-stat is-promo"><div class="cc-dir-stat-l"><span class="cc-label">WITH COMPLIANCE</span><div class="cc-dir-stat-num">{{ complianceCount }}</div></div><div class="cc-dir-stat-spark"><svg viewBox="0 0 64 28" preserveAspectRatio="none" style="width:64px;height:28px;color:var(--cc-warn)"><polyline points="0,18 16,20 32,10 48,12 64,5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></div></div>
        <div class="cc-dir-stat"><div class="cc-dir-stat-l"><span class="cc-label">SHOWING · NOW</span><div class="cc-dir-stat-num">{{ filtered.length.toLocaleString() }}</div></div><div class="cc-dir-stat-spark"><svg viewBox="0 0 64 28" preserveAspectRatio="none" style="width:64px;height:28px;color:var(--cc-accent-2)"><polyline points="0,14 16,12 32,16 48,11 64,9" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></div></div>
      </div>

      <!-- Toolbar -->
      <div class="cc-dir-bar">
        <div class="cc-dir-search">
          <span class="cc-dir-search-ico"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16zM21 21l-4.3-4.3"/></svg></span>
          <input v-model="q" placeholder="Search products, suppliers, categories…" />
        </div>
        <button v-if="anyFilter" class="cc-dir-pill" @click="clearAll">Clear filters</button>
        <div class="cc-dir-view">
          <button :class="{ on: view === 'list' }" @click="view = 'list'" title="List"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg></button>
          <button :class="{ on: view === 'grid' }" @click="view = 'grid'" title="Grid"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z"/></svg></button>
        </div>
      </div>

      <!-- Body -->
      <div class="cc-dir-grid">
        <aside class="cc-dir-facets">
          <div v-for="f in [
            { label: 'CATEGORY', items: catFacets, set: selCats, t: toggleCat },
            { label: 'SUPPLIER', items: supFacets, set: selSup, t: toggleSup },
            { label: 'DEPLOYMENT', items: depFacets, set: selDep, t: toggleDep },
            { label: 'COMPLIANCE', items: compFacets, set: selComp, t: toggleComp },
          ]" :key="f.label" class="cc-dir-facet">
            <h4>{{ f.label }} <em>{{ f.items.length }}</em></h4>
            <div v-for="[val, n] in f.items" :key="val" class="cc-dir-facet-row" :class="{ 'is-on': f.set.has(val) }" @click="f.t(val)"><span class="cc-dir-facet-cb"></span><span>{{ val }}</span><span class="cc-dir-facet-cnt">{{ n }}</span></div>
            <div v-if="!f.items.length" style="font-size:11px;color:var(--cc-text-3);font-style:italic">None</div>
          </div>
        </aside>

        <div style="min-width:0">
          <div v-if="loading" class="space-y-2"><div v-for="i in 8" :key="i" class="bg-cc-elevated border border-cc-border rounded-cc h-14 animate-pulse"></div></div>
          <div v-else-if="error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 p-4 rounded-cc text-center">{{ error }}</div>

          <!-- LIST -->
          <div v-else-if="view === 'list'" class="cc-prd-list">
            <div class="cc-prd-headrow"><span></span><span>PRODUCT</span><span>CATEGORY · SUPPLIER</span><span>PRICING</span><span>DEPLOY · COMPLIANCE</span><span style="text-align:right">ACTIONS</span></div>
            <div v-for="p in shown" :key="p.id" class="cc-prd-row" @click="openProduct(p)">
              <div class="h-9 w-9 bg-white rounded-cc flex items-center justify-center overflow-hidden"><SupplierLogo :src="p.supplier.logoUrl" :website="p.supplier.websiteUrl" :alt="p.supplier.name" class="object-contain max-h-full max-w-full" /></div>
              <div class="cc-prd-name"><div class="cc-prd-name-t"><span class="t">{{ p.name }}</span></div><div class="cc-prd-name-d">{{ p.description }}</div></div>
              <div class="cc-prd-catcell"><span class="cc-prd-cat">{{ prodCat(p) }}</span><span class="cc-prd-supplier">{{ p.supplier.name }}</span></div>
              <div class="cc-prd-pricecell"><span class="cc-prd-pricing">{{ pricing(p) }}</span></div>
              <div class="cc-prd-meta"><span class="cc-prd-deploy">{{ p.deploymentModel || '—' }}</span><div class="cc-prd-compliance"><span v-if="!p.complianceCertifications.length" class="none">—</span><span v-for="c in p.complianceCertifications.slice(0,4)" v-else :key="c">{{ c }}</span></div></div>
              <div class="cc-dir-act" @click.stop><!-- Hidden for now; drop v-if to restore --><button v-if="false" class="primary" @click="openProduct(p)">QUOTE</button></div>
            </div>
            <div v-if="!filtered.length" class="cc-prd-empty">No products match the current filters.</div>
            <div v-if="filtered.length" class="cc-prd-foot"><span>SHOWING <b style="color:var(--cc-text)">{{ shown.length }}</b> OF <b style="color:var(--cc-text)">{{ filtered.length.toLocaleString() }}</b> PRODUCTS</span><span>{{ anyFilter ? 'FILTERED' : 'ALL CATEGORIES' }}</span></div>
          </div>

          <!-- GRID -->
          <div v-else class="cc-prd-cards">
            <div v-for="p in shown" :key="p.id" class="cc-prd-card" @click="openProduct(p)">
              <div class="cc-prd-card-head">
                <div class="h-9 w-9 bg-white rounded-cc flex items-center justify-center overflow-hidden shrink-0"><SupplierLogo :src="p.supplier.logoUrl" :website="p.supplier.websiteUrl" :alt="p.supplier.name" class="object-contain max-h-full max-w-full" /></div>
                <div class="cc-prd-card-id"><div class="cc-prd-card-name">{{ p.name }}</div><div class="cc-prd-card-sub">{{ p.supplier.name }}</div></div>
              </div>
              <div class="cc-prd-card-desc">{{ p.description }}</div>
              <div class="cc-prd-card-chips"><span class="cc-prd-cat">{{ prodCat(p) }}</span><span v-if="p.deploymentModel" class="cc-prd-deploy">{{ p.deploymentModel }}</span></div>
              <div class="cc-prd-card-foot"><span class="cc-prd-card-price">{{ pricing(p) }}</span><button v-if="false" class="cc-prd-card-quote" @click.stop="openProduct(p)">QUOTE</button></div>
            </div>
            <div v-if="!filtered.length" class="cc-prd-empty">No products match the current filters.</div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
